import React from 'react';
import { moduleApi } from '../../../lib/api';
import { Guest } from './guestFilters';

export interface ParsedRow { first_name: string; last_name: string; email: string; }
export interface SendProgress { done: number; total: number; sent: number; failed: number; lastError: string }
export interface ImportResult { imported: number; duplicates_skipped: number; invalid_skipped: number }

// Un Worker Cloudflare a un temps d'exécution limité — envoyer les 350 emails d'un événement en un seul
// appel serait risqué. Le bouton reste "une seule action" côté commerciale : c'est le navigateur qui
// enchaîne les lots, pas elle qui doit cliquer 350 fois.
export const SEND_BATCH_SIZE = 25;

/**
 * Les quatre appels que le hook enchaîne. Par défaut ceux de l'établissement (moduleApi) ; le Portail
 * Organisateur (lot 10) fournit les siens, sur son lien et sa session — même logique, mêmes lots de 25,
 * seule l'adresse change. `send` doit JETER une erreur quand le serveur refuse (plafond, relance trop
 * tôt…) : le hook la garde dans `sendProgress.lastError` pour que l'écran puisse la dire.
 */
export interface ConvivesApi {
    list: () => Promise<Guest[]>;
    import: (rows: ParsedRow[]) => Promise<ImportResult | any>;
    remove: (guestId: string) => Promise<any>;
    send: (guestIds: string[], mode: 'invitation' | 'relance') => Promise<{ sent?: number; failed?: number; last_error?: string } | any>;
}

const apiEtablissement = (eventId: string): ConvivesApi => ({
    list: () => moduleApi.getEvenementielGuests(eventId),
    import: (rows) => moduleApi.importEvenementielGuests(eventId, rows),
    remove: (guestId) => moduleApi.deleteEvenementielGuest(eventId, guestId),
    send: (guestIds) => moduleApi.sendEvenementielInvitations(eventId, guestIds),
});

/**
 * Tout ce que Menus & Convives sait FAIRE sur la liste d'un événement : charger, importer, envoyer par
 * lots de 25, relancer, retirer, clôturer + valider. Extrait tel quel de l'ancienne `ConvivesImportSection`
 * (refonte design lot 9) pour que la page 18b ne porte que de l'affichage — aucune règle ne change ici,
 * ce sont les mêmes routes, dans le même ordre.
 */
export function useConvivesActions(eventId: string, apiFournie?: ConvivesApi) {
    const api = React.useMemo(() => apiFournie || apiEtablissement(eventId), [eventId, apiFournie]);
    const [guests, setGuests] = React.useState<Guest[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState('');
    const [sending, setSending] = React.useState(false);
    const [sendProgress, setSendProgress] = React.useState<SendProgress | null>(null);
    const [closing, setClosing] = React.useState(false);
    const [importing, setImporting] = React.useState(false);

    const load = React.useCallback(() => {
        api.list()
            .then((g: any) => setGuests(Array.isArray(g) ? g : []))
            .catch(() => setGuests([]))
            .finally(() => setLoading(false));
    }, [api]);
    React.useEffect(() => { load(); }, [load]);

    const importRows = async (rows: ParsedRow[]): Promise<ImportResult> => {
        setImporting(true);
        setError('');
        try {
            const res: any = await api.import(rows);
            load();
            return { imported: res?.imported || 0, duplicates_skipped: res?.duplicates_skipped || 0, invalid_skipped: res?.invalid_skipped || 0 };
        } finally {
            setImporting(false);
        }
    };

    const removeGuest = async (guestId: string) => {
        try {
            await api.remove(guestId);
            load();
        } catch (e: any) {
            setError(e?.message || 'Erreur lors de la suppression.');
        }
    };

    const sendBatch = async (guestIds: string[], mode: 'invitation' | 'relance' = 'invitation') => {
        setSending(true);
        setError('');
        const totals: SendProgress = { done: 0, total: guestIds.length, sent: 0, failed: 0, lastError: '' };
        setSendProgress({ ...totals });
        for (let i = 0; i < guestIds.length; i += SEND_BATCH_SIZE) {
            const batch = guestIds.slice(i, i + SEND_BATCH_SIZE);
            try {
                const res: any = await api.send(batch, mode);
                totals.sent += res?.sent || 0;
                totals.failed += res?.failed || 0;
                if (res?.last_error) totals.lastError = res.last_error;
            } catch (e: any) {
                totals.failed += batch.length;
                if (e?.message) totals.lastError = e.message;
            }
            totals.done += batch.length;
            setSendProgress({ ...totals });
        }
        setSending(false);
        load();
    };

    const notYetInvited = guests.filter((g) => !g.invitation_sent_at);
    const unresponded = guests.filter((g) => g.invitation_sent_at && !g.responded_at);
    const nonResponded = guests.filter((g) => !g.responded_at);

    const sendAllInvitations = () => sendBatch(notYetInvited.map((g) => g.id));
    const remindUnresponded = () => sendBatch(unresponded.map((g) => g.id), 'relance');
    const resendOne = (guestId: string) => sendBatch([guestId], 'relance');

    // "Clôturer & Valider" — fusion de deux actions (Demande explicite d'Emeni, 2026-08-24) :
    // 1. Ferme les réponses (responses_closed_at) : bloque non-répondants de répondre
    // 2. Valide/verrouille (production_locked_at) : empêche TOUS les convives de modifier après jour J
    // Les convives non-répondants reçoivent un email configurable les orientant vers l'organisateur.
    const closeResponses = async () => {
        setClosing(true);
        setError('');
        try {
            await moduleApi.closeEvenementielCollecte(eventId);
            await moduleApi.validateEvenementielCollecte(eventId);
            const ids = nonResponded.map((g) => g.id);
            const totals: SendProgress = { done: 0, total: ids.length, sent: 0, failed: 0, lastError: '' };
            setSendProgress({ ...totals });
            for (let i = 0; i < ids.length; i += SEND_BATCH_SIZE) {
                const batch = ids.slice(i, i + SEND_BATCH_SIZE);
                try {
                    const res: any = await moduleApi.closeNotifyEvenementielConvives(eventId, batch);
                    totals.sent += res?.sent || 0;
                    totals.failed += res?.failed || 0;
                    if (res?.last_error) totals.lastError = res.last_error;
                } catch {
                    totals.failed += batch.length;
                }
                totals.done += batch.length;
                setSendProgress({ ...totals });
            }
        } catch (e: any) {
            setError(e?.message || 'Erreur lors de la clôture et validation.');
        } finally {
            setClosing(false);
            load();
        }
    };

    return {
        guests, loading, error, setError, reload: load,
        importing, importRows, removeGuest,
        sending, sendProgress, sendAllInvitations, remindUnresponded, resendOne,
        closing, closeResponses,
        counts: { total: guests.length, notYetInvited: notYetInvited.length, unresponded: unresponded.length, nonResponded: nonResponded.length, responded: guests.filter((g) => !!g.responded_at).length },
    };
}
